// ─── Timeline → RenderInputProps serializer ─────────────────────────────────
// Turns the editor's in-memory state (clips, aspectRatio, duration) into the
// JSON payload the Remotion backend consumes. This is the ONLY place that
// knows how to:
//   • Resolve a playable URL for every media clip (re-signing from storage
//     path/asset id so the URL stays valid throughout a long render).
//   • Drop clips that can't render (empty text, AI still generating, etc.)
//     and report them back so the UI can warn the user.
//   • Strip editor-only fields (name, color, thumbnail, aiStatus, UI flags)
//     the renderer doesn't need.
//
// The resulting object matches `RenderInputProps` in
// `remotion/src/types.ts`. Keep both in sync.

import { getAssetSignedUrl } from '../../utils/projectAssets';
import { supabase } from '../supabase';
import type {
  RenderAspectRatio,
  RenderClip,
  RenderClipType,
  RenderTransitionType,
  SerializeResult,
  SkippedClip,
} from './types';

// Shape of the editor's in-memory clip. We don't import it to avoid a tight
// coupling to VideoEditor.tsx (which has a LOT of UI-only fields); instead
// we accept `any` for unknown optional keys and only read what we need.
// The fields below MUST match `TimelineClip` in
// `project/src/pages/VideoEditor.tsx`.
export interface EditorClipInput {
  id: string;
  type: RenderClipType;
  name?: string;
  startTime: number;
  duration: number;
  src?: string;
  mediaOffset?: number;
  volume?: number;
  trackIndex?: number;
  transitionIn?: {
    type: RenderTransitionType;
    durationSec: number;
  };
  storagePath?: string;
  assetId?: string;
  aiStatus?: 'draft' | 'generating' | 'ready' | 'error';
  // Text clip fields
  text?: string;
  fontFamily?: string;
  fontSizePct?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  textXPct?: number;
  textYPct?: number;
}

export interface SerializeInput {
  clips: EditorClipInput[];
  aspectRatio: RenderAspectRatio;
  /** Total timeline length in seconds. */
  durationInSeconds: number;
  /** Defaults to 30. */
  fps?: number;
}

// Resolve a playable HTTPS URL for a single media clip. Returns null if
// no source can be found / signed. The caller decides whether to skip
// the clip or hard-fail the export.
async function resolveClipUrl(
  clip: EditorClipInput,
): Promise<{ url: string | null; reason?: string }> {
  // 1. Prefer `storagePath` — most reliable, re-signable on every export.
  if (clip.storagePath) {
    const signed = await getAssetSignedUrl(clip.storagePath);
    if (signed) return { url: signed };
    return { url: null, reason: 'signed-url-failed (storagePath)' };
  }
  // 2. Fall back to looking up by assetId → storage_path → sign.
  if (clip.assetId) {
    const { data, error } = await supabase
      .from('video_project_assets')
      .select('storage_path')
      .eq('id', clip.assetId)
      .maybeSingle();
    if (!error && data?.storage_path) {
      const signed = await getAssetSignedUrl(data.storage_path as string);
      if (signed) return { url: signed };
      return { url: null, reason: 'signed-url-failed (assetId)' };
    }
  }
  // 3. Last resort: use whatever `src` the clip already carries. This is
  //    the path for AI-generated clips whose output lives on an external
  //    bucket (R2/S3) and isn't mirrored in video_project_assets. If the
  //    URL is short-lived, the render backend will fetch it before expiry
  //    when firing the render.
  if (clip.src && /^https?:\/\//i.test(clip.src)) return { url: clip.src };

  return { url: null, reason: 'no storagePath/assetId/src' };
}

// Map an editor clip to a renderer clip. Returns either a `RenderClip`
// ready to send, or a `SkippedClip` reason if it can't render.
async function mapClip(
  clip: EditorClipInput,
): Promise<RenderClip | SkippedClip> {
  // ── Text clips ────────────────────────────────────────────────────────
  if (clip.type === 'text') {
    const text = (clip.text ?? '').trim();
    if (!text) {
      return { id: clip.id, name: clip.name, reason: 'text-empty' };
    }
    return {
      id: clip.id,
      type: 'text',
      startTime: clip.startTime,
      duration: clip.duration,
      text: clip.text,
      fontFamily: clip.fontFamily,
      fontSizePct: clip.fontSizePct,
      fontWeight: clip.fontWeight,
      fontStyle: clip.fontStyle,
      textColor: clip.textColor,
      textAlign: clip.textAlign,
      textXPct: clip.textXPct,
      textYPct: clip.textYPct,
    };
  }

  // ── AI-generated clips that aren't ready yet are silently skipped.
  //    The UI-level preflight (see `preflightExport`) surfaces these so the
  //    user can wait for generation to finish before retrying.
  if (clip.type === 'ai-generated' && clip.aiStatus && clip.aiStatus !== 'ready') {
    return { id: clip.id, name: clip.name, reason: 'ai-not-ready', detail: clip.aiStatus };
  }

  // ── Media clips (video/image/audio/ai-generated-ready): resolve URL.
  const { url, reason } = await resolveClipUrl(clip);
  if (!url) {
    return {
      id: clip.id,
      name: clip.name,
      reason: reason?.startsWith('signed-url') ? 'signed-url-failed' : 'missing-source',
      detail: reason,
    };
  }

  return {
    id: clip.id,
    type: clip.type,
    startTime: clip.startTime,
    duration: clip.duration,
    src: url,
    mediaOffset: clip.mediaOffset,
    volume: clip.volume,
    trackIndex: clip.trackIndex,
    transitionIn: clip.transitionIn,
  };
}

// Public API: serialize the editor timeline into the exact payload the
// Remotion backend will consume. Does NOT hit the render backend itself —
// the caller passes the returned `input` to whatever endpoint kicks off
// the render job.
export async function serializeTimeline(
  params: SerializeInput,
): Promise<SerializeResult> {
  const { clips, aspectRatio, durationInSeconds, fps = 30 } = params;

  // Resolve all clip URLs in parallel — signing N URLs sequentially would
  // push export latency into multi-second territory on large projects.
  const mapped = await Promise.all(clips.map(c => mapClip(c)));

  const out: RenderClip[] = [];
  const skipped: SkippedClip[] = [];
  for (const m of mapped) {
    if ('reason' in m) skipped.push(m);
    else out.push(m);
  }

  return {
    input: {
      clips: out,
      aspectRatio,
      durationInSeconds,
      fps,
    },
    skipped,
  };
}

// Lightweight preflight that checks the timeline WITHOUT hitting network.
// Useful for the export modal to warn the user about obvious issues before
// they commit to a render (e.g. "5 AI clips still generating").
export function preflightExport(params: {
  clips: EditorClipInput[];
  durationInSeconds: number;
}): {
  blockers: string[];
  warnings: string[];
} {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const { clips, durationInSeconds } = params;

  if (clips.length === 0) {
    blockers.push('La timeline está vacía — agregá al menos un clip antes de exportar.');
  }
  if (!isFinite(durationInSeconds) || durationInSeconds <= 0) {
    blockers.push('La duración del proyecto no es válida.');
  }

  const aiPending = clips.filter(
    c => c.type === 'ai-generated' && c.aiStatus && c.aiStatus !== 'ready',
  );
  if (aiPending.length > 0) {
    blockers.push(
      `Hay ${aiPending.length} clip(s) de IA que todavía no terminaron de generarse. ` +
        `Esperá a que terminen o eliminalos antes de exportar.`,
    );
  }

  const textEmpty = clips.filter(c => c.type === 'text' && !(c.text ?? '').trim());
  if (textEmpty.length > 0) {
    warnings.push(
      `${textEmpty.length} clip(s) de texto están vacíos y se van a omitir del MP4 final.`,
    );
  }

  const mediaNoSource = clips.filter(
    c =>
      c.type !== 'text' &&
      !c.storagePath &&
      !c.assetId &&
      !(c.src && /^https?:\/\//i.test(c.src)),
  );
  if (mediaNoSource.length > 0) {
    warnings.push(
      `${mediaNoSource.length} clip(s) de medio no tienen URL resoluble y se van a omitir.`,
    );
  }

  return { blockers, warnings };
}
